import { useEffect, useState, useRef } from "react";
import "../css/Dropdown.css";
import { inputOnEnter } from "../js/utils";

export default function Dropdown({
  id,
  icon,
  size,
  disabled,
  mandatory = true,
  optionList = [],
  op = null,
  tabIndex,
  onChange,
}) {
  const [togglePanel, setTogglePanel] = useState(false);
  const [inputSearch, setInputSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filteredList, setFilteredList] = useState(optionList);

  const dropdownRef = useRef(null);
  const mouseInside = useRef(false);
  const inputRef = useRef(null);

  const getComboboxSize = () => {
    const sizes = [
      "combobox-full",
      "combobox-small",
      "combobox-medium",
      "combobox-large",
      "combobox-super-small",
    ];
    return sizes[size] || "combobox-default";
  };

  const scrollToSelected = () => {
    const items = dropdownRef.current?.querySelectorAll(".dropdown-op") || [];
    const selected = items[selectedIndex];
    selected?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const chooseOption = (item, returnFocus = true) => {
    if (!item) return;
    setInputSearch(item.text);
    setTogglePanel(false);
    onChange(item);
    if (returnFocus) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const stripAccents = (text) =>
    text.normalize("NFD").replace(/\p{Diacritic}/gu, "");

  const applyFilter = (text) => {
    const upperText = stripAccents(text.toUpperCase());
    const filtered = optionList.filter((item) =>
      stripAccents(item.text).includes(upperText),
    );
    setFilteredList(filtered);
    /* setSelectedIndex(0); */
    const currentIndex = filtered.findIndex(
      (item) => item.text === inputSearch,
    );
    setSelectedIndex(currentIndex >= 0 ? currentIndex : 0);
    setTogglePanel(true);
  };

  const handleKey = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!filteredList.length) return;
        setSelectedIndex((i) => (i + 1) % filteredList.length);
        break;

      case "ArrowUp":
        e.preventDefault();
        if (!filteredList.length) return;
        setSelectedIndex(
          (i) => (i - 1 + filteredList.length) % filteredList.length,
        );
        break;

      case "Enter":
        e.preventDefault();
        if (togglePanel && filteredList.length) {
          chooseOption(filteredList[selectedIndex]);
        } else {
          inputOnEnter(e);
        }
        break;

      case "Escape":
        setTogglePanel(false);
        break;

      case "Tab":
        if (mandatory) {
          chooseOption(filteredList[selectedIndex] || null, false);
        } else {
          onChange({ id: inputSearch, text: inputSearch });
        }
        inputOnEnter(e);
        break;

      default:
        if (!mandatory) {
          onChange({ id: inputSearch, text: inputSearch });
        }
    }
  };

  const handleUnfocus = () => {
    if (!mandatory) {
      onChange({ id: inputSearch, text: inputSearch });
      return;
    }

    const exactMatch = optionList.find((item) => item.text === inputSearch);
    if (exactMatch) {
      chooseOption(exactMatch, false);
      return;
    }

    let text = stripAccents(inputSearch);
    let match = null;
    while (text.length > 0 && !match) {
      match = optionList.find((item) =>
        stripAccents(item.text).startsWith(text),
      );
      if (!match) text = text.slice(0, -1);
    }

    chooseOption(match || optionList[0] || null, false);
  };

  // Effects
  useEffect(() => {
    setFilteredList(optionList);
  }, [optionList]);

  useEffect(() => {
    if (op == null) return;
    const selected = mandatory
      ? optionList.find((item) => item.id === op || item.value === op)
      : { id: op, text: op };
    setInputSearch(selected ? selected.text : "");
  }, [op, optionList]);

  useEffect(scrollToSelected, [selectedIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTogglePanel(false);
      }
    };

    if (togglePanel) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [togglePanel]);

  return (
    <div
      id={id}
      className={`combobox ${getComboboxSize()}`}
      ref={dropdownRef}
      onBlur={() => !mouseInside.current && handleUnfocus()}
      onMouseEnter={() => (mouseInside.current = true)}
      onMouseLeave={() => (mouseInside.current = false)}
    >
      <div
        className={`dropdown pure-dropdown ${
          togglePanel ? "dropdown-open" : ""
        } ${disabled ? "dropdown-disabled" : ""}`}
        onClick={() => {
          if (!disabled) {
            setFilteredList(optionList);

            const currentIndex = optionList.findIndex(
              (item) => item.text === inputSearch,
            );
            setSelectedIndex(currentIndex >= 0 ? currentIndex : 0);

            setTogglePanel((open) => !open);
          }
        }}
      >
        {icon && <img src={icon} alt="icon" />}
        <div className="languagetext">
          <input
            ref={inputRef}
            className="text"
            tabIndex={tabIndex}
            value={inputSearch}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              setInputSearch(value);
              applyFilter(value);
            }}
            onKeyUp={handleKey}
            onKeyDown={(e) => e.key === "Tab" && handleKey(e)}
            disabled={disabled}
            autoComplete="off"
          />
        </div>
        <div className="downicon">
          <img
            src="/images/blue-down-arrow.svg"
            loading="lazy"
            alt="Panel icon"
            className="vectors-wrapper-11"
          />
        </div>
      </div>

      {togglePanel && (
        <div className="dropdown-options">
          {filteredList.length === 0 ? (
            <></>
          ) : (
            filteredList.map((item, i) => (
              <div
                key={i}
                className={`dropdown-op ${
                  i === selectedIndex ? "selected-op" : ""
                }`}
                onClick={(e) => {
                  chooseOption(item);
                  e.stopPropagation();
                }}
                onTouchEnd={(e) => {
                  chooseOption(item);
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <span>{item.text}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
